import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { MatDatepicker, MatDatepickerInput, MatDatepickerToggle } from '@angular/material/datepicker';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { ConsultaList } from 'src/app/shared/model/core/ConsultaList.model';
import { Denuncia, EstatusDenuncia } from 'src/app/shared/model/denuncias/denuncia.model';
import { KanbanDenuncia } from 'src/app/shared/model/denuncias/kanban-denuncia.model';
import { CategoriaDenuncia } from 'src/app/shared/model/denuncias/categoria-denuncia.model';
import { DenunciasService } from '../denuncias.service';

@Component({
  selector: 'app-kanban-denuncias',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatDatepicker,
    MatSuffix
  ],
  templateUrl: './kanban-denuncias.component.html',
  styleUrls: ['./kanban-denuncias.component.css']
})
export class KanbanDenunciasComponent implements OnInit {

  columnas: KanbanDenuncia[] = [];
  categorias: CategoriaDenuncia[] = [];
  cargando: boolean = false;

  // Filtros
  busqueda: string = '';
  id_categoria: number = null;
  fechaInicio: Date | null = null;
  fechaFin: Date | null = null;

  // Drag & drop
  draggingDenuncia: Denuncia = null;

  // Paginación por columna (client-side)
  tamPagina: number = 4;
  paginasColumna: number[] = [1, 1, 1];

  constructor(
    private denunciasService: DenunciasService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.inicializarColumnas();
    this.cargarCategorias();
    this.cargarKanban();
  }

  inicializarColumnas(): void {
    this.columnas = [
      new KanbanDenuncia('Recibida', [], 'mail', '#c8102e'),
      new KanbanDenuncia('En Investigación', [], 'manage_search', '#d97706'),
      new KanbanDenuncia('Cerrada', [], 'task_alt', '#15803d')
    ];
  }

  cargarCategorias(): void {
    this.denunciasService.consultarCategorias().subscribe(resp => {
      this.categorias = resp?.list || [];
      this.syncView();
    }, () => {
      this.categorias = [];
      this.syncView();
    });
  }

  cargarKanban(): void {
    this.cargando = true;
    this.inicializarColumnas();
    this.paginasColumna = [1, 1, 1];
    this.syncView();

    const req = new ConsultaList<Denuncia>();
    if (this.busqueda)      { req.param1 = this.busqueda; }
    if (this.id_categoria)  { req.param4 = this.id_categoria; }
    if (this.fechaInicio)   { req.fecha_ini = this.construirFechaLocal(this.fechaInicio, false); }
    if (this.fechaFin)      { req.fecha_fin = this.construirFechaLocal(this.fechaFin, true); }

    this.denunciasService.consultarDenunciasKanban(req).subscribe(
      resp => {
        this.cargando = false;
        const lista = resp?.list || [];
        this.columnas[0].lista = lista.filter(d => d.id_estatus === EstatusDenuncia.RECIBIDA);
        this.columnas[1].lista = lista.filter(d => d.id_estatus === EstatusDenuncia.EN_INVESTIGACION);
        this.columnas[2].lista = lista.filter(d => d.id_estatus === EstatusDenuncia.CERRADA);
        this.syncView();
      },
      () => {
        this.cargando = false;
        this.inicializarColumnas();
        this.syncView();
      }
    );
  }

  limpiarFiltros(): void {
    this.busqueda = '';
    this.id_categoria = null;
    this.fechaInicio = null;
    this.fechaFin = null;
    this.cargarKanban();
  }

  verDetalle(denuncia: Denuncia): void {
    this.router.navigate(['/DENUNCIAS/detalle', denuncia.id_denuncia]);
  }

  irConfiguracion(): void {
    this.router.navigate(['/DENUNCIAS/configuracion']);
  }

  // Drag & Drop

  /** Una denuncia Cerrada no se puede reabrir: queda bloqueada para moverla. */
  esBloqueada(denuncia: Denuncia): boolean {
    return denuncia?.id_estatus === EstatusDenuncia.CERRADA;
  }

  onDragStart(denuncia: Denuncia): void {
    if (this.esBloqueada(denuncia)) {
      this.draggingDenuncia = null;
      return;
    }
    this.draggingDenuncia = denuncia;
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  onDrop(event: DragEvent, columnaIndex: number): void {
    event.preventDefault();
    if (!this.draggingDenuncia) { return; }

    const nuevoEstatus = columnaIndex + 1 as EstatusDenuncia;
    if (this.draggingDenuncia.id_estatus === nuevoEstatus) { return; }

    // Una denuncia Cerrada no puede regresar a Recibida ni a En Investigación.
    if (this.esBloqueada(this.draggingDenuncia)) {
      this.draggingDenuncia = null;
      return;
    }

    const req = new Denuncia();
    req.id_denuncia = this.draggingDenuncia.id_denuncia;
    req.id_estatus = nuevoEstatus;
    req.idEstatusAnterior = this.draggingDenuncia.id_estatus;

    this.denunciasService.actualizarEstatus(req).subscribe(
      resp => {
        if (resp?.pCodRetorno === '0') {
          this.cargarKanban();
        }
      }
    );

    this.draggingDenuncia = null;
  }

  getBadgeClass(id_estatus: number): string {
    const map = { 1: 'estatus-recibida', 2: 'estatus-investigacion', 3: 'estatus-cerrada' };
    return map[id_estatus] || '';
  }

  getTotalVisible(): number {
    return this.columnas.reduce((total, columna) => total + columna.lista.length, 0);
  }

  getTipoLabel(denuncia: Denuncia): string {
    return denuncia.tipoDenuncia === 'CONFIDENCIAL' ? 'Confidencial' : 'Anónima';
  }

  getHechosResumen(denuncia: Denuncia): string {
    const hechos = (denuncia.hechos || '').trim();
    if (!hechos) {
      return 'Sin descripción registrada.';
    }

    return hechos.length > 120 ? `${hechos.slice(0, 117)}...` : hechos;
  }

  trackByColumn(_: number, columna: KanbanDenuncia): string {
    return columna.titulo;
  }

  // ── Paginación por columna (client-side) ───────────────────────────────
  itemsPagina(columna: KanbanDenuncia, index: number): Denuncia[] {
    const lista = columna?.lista || [];
    const inicio = (this.paginaActual(index) - 1) * this.tamPagina;
    return lista.slice(inicio, inicio + this.tamPagina);
  }

  totalPaginas(columna: KanbanDenuncia): number {
    const total = columna?.lista?.length || 0;
    return total > 0 ? Math.ceil(total / this.tamPagina) : 1;
  }

  paginaActual(index: number): number {
    const total = this.totalPaginas(this.columnas[index]);
    const actual = this.paginasColumna[index] || 1;
    return Math.min(Math.max(actual, 1), Math.max(total, 1));
  }

  rangoInicio(index: number): number {
    const total = this.columnas[index]?.lista?.length || 0;
    return total === 0 ? 0 : (this.paginaActual(index) - 1) * this.tamPagina + 1;
  }

  rangoFin(index: number): number {
    const total = this.columnas[index]?.lista?.length || 0;
    return Math.min(this.paginaActual(index) * this.tamPagina, total);
  }

  irPagina(index: number, pagina: number): void {
    const total = this.totalPaginas(this.columnas[index]);
    if (pagina < 1 || pagina > total || pagina === this.paginaActual(index)) { return; }
    this.paginasColumna[index] = pagina;
  }

  primeraPagina(index: number): void { this.irPagina(index, 1); }
  paginaAnterior(index: number): void { this.irPagina(index, this.paginaActual(index) - 1); }
  paginaSiguiente(index: number): void { this.irPagina(index, this.paginaActual(index) + 1); }
  ultimaPagina(index: number): void { this.irPagina(index, this.totalPaginas(this.columnas[index])); }

  getPaginationItems(index: number): Array<number | string> {
    const totalPaginas = this.totalPaginas(this.columnas[index]);
    if (totalPaginas <= 1) { return [1]; }

    const actual = this.paginaActual(index);
    const paginas = new Set<number>([1, totalPaginas, actual - 1, actual, actual + 1]);
    const ordenadas = Array.from(paginas)
      .filter(p => p >= 1 && p <= totalPaginas)
      .sort((a, b) => a - b);

    const items: Array<number | string> = [];
    for (let i = 0; i < ordenadas.length; i++) {
      const p = ordenadas[i];
      const previa = ordenadas[i - 1];
      if (previa && p - previa > 1) { items.push(`ellipsis-${previa}-${p}`); }
      items.push(p);
    }
    return items;
  }

  isPageNumber(item: number | string): item is number {
    return typeof item === 'number';
  }

  private construirFechaLocal(fecha: Date, finDia: boolean): Date {
    const year = fecha.getFullYear();
    const month = fecha.getMonth();
    const day = fecha.getDate();
    return finDia
      ? new Date(year, month, day, 23, 59, 59, 999)
      : new Date(year, month, day, 0, 0, 0, 0);
  }

  private syncView(): void {
    this.cdr.detectChanges();
  }
}
